import { format, parseISO } from 'date-fns';
import { DatePrecision } from '@/features/documents/types';

/**
 * Formats a document date string based on its precision.
 * Handles potential timezone issues by parsing YYYY-MM-DD as UTC.
 * 
 * @param dateString The date string in 'YYYY-MM-DD' format from the database.
 * @param precision The precision of the date ('year', 'month', 'day').
 * @returns A formatted date string for display.
 */
export function formatDocumentDate(
  dateString: string | null | undefined,
  precision: DatePrecision | null | undefined
): string {
  if (!dateString) {
    return 'N/A';
  }

  // Ensure the date string is treated as UTC to avoid timezone shifts
  // by explicitly parsing it. If it's already a full ISO string with timezone,
  // parseISO will handle it. If it's just YYYY-MM-DD, we need to be careful.
  
  // For year and month precision, we don't need to parse into a Date object
  // which can cause timezone issues. We can just use the string parts.
  const parts = dateString.split('-');
  const year = parts[0];

  if (precision === 'year') {
    return year || 'Invalid Date';
  }

  if (precision === 'month' && parts.length >= 2) {
    const monthIndex = parseInt(parts[1], 10) - 1;
    if (year && !isNaN(monthIndex) && monthIndex >= 0 && monthIndex <= 11) {
      // Create a date object safely for formatting month name
      const dateForMonthName = new Date(Date.UTC(parseInt(year), monthIndex, 1));
      return format(dateForMonthName, 'MMMM yyyy');
    }
    return `${year}-${parts[1]}` || 'Invalid Date'; // Fallback if month is odd
  }

  // For 'day' precision or if precision is unknown (treat as day for safety)
  // We parse the date string. Assuming it's YYYY-MM-DD from the DB.
  // To avoid local timezone interpretation, we can parse it as UTC.
  // One way is to append 'T00:00:00Z' if it's just a date string.
  // However, date-fns's parseISO is generally robust.
  // Let's ensure we handle the "YYYY-MM-DD" case carefully.
  try {
    // If dateString is "YYYY-MM-DD", parseISO might interpret it in local time.
    // To force UTC, we can construct the Date object with UTC components.
    if (parts.length === 3) {
        const yearNum = parseInt(parts[0]);
        const monthNum = parseInt(parts[1]) -1; // month is 0-indexed
        const dayNum = parseInt(parts[2]);
        if (!isNaN(yearNum) && !isNaN(monthNum) && !isNaN(dayNum)) {
            const utcDate = new Date(Date.UTC(yearNum, monthNum, dayNum));
            return format(utcDate, 'MMMM d, yyyy');
        }
    }
    // Fallback for other formats or if parts are not as expected
    const parsedDate = parseISO(dateString);
    return format(parsedDate, 'MMMM d, yyyy');
  } catch (error) {
    // Suppress console errors for invalid dates like "19XX" - handled gracefully
    // console.error('Error formatting date:', dateString, error);
    return dateString; // Fallback to raw string if formatting fails
  }
}
